/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Style } from '@opengeoweb/webmap';
import type { STACColormapEntry, STACRenderEntry } from '../api';

/**
 * Converts a STAC render extension colormap to an OpenLayers WebGLTile palette style.
 *
 * The STAC colormap maps pixel values (0–255) to RGBA arrays where each channel is 0–255.
 * The returned object is suitable for use as the `style` option of `ol/layer/WebGLTile`.
 */
export const stacColormapToOlStyle = (
  colormap: Record<string, STACColormapEntry>,
): object => {
  const palette: string[] = Array.from({ length: 256 }, (_, i) => {
    const entry = colormap[String(i)];
    if (!entry) {
      return 'rgba(0,0,0,0)';
    }
    const [red, green, blue, alphaChannel] = entry;
    const alpha = (alphaChannel / 255).toFixed(4);
    return `rgba(${red},${green},${blue},${alpha})`;
  });

  return {
    color: ['palette', ['band', 1], palette],
  };
};

/**
 * Returns a grayscale WebGLTile style where band-1 value maps from black (0)
 * to white (255). Used as a safe default when no STAC render colormap exists.
 */
export const stacGrayscaleOlStyle = (): object => {
  const palette: string[] = Array.from(
    { length: 256 },
    (_, i) => `rgba(${i},${i},${i},1.0000)`,
  );

  return {
    color: ['palette', ['band', 1], palette],
  };
};

/**
 * Returns a grayscale style for normalized GeoTIFF samples where `['band', 1]`
 * is expected in [0, 1]. This avoids white-only output when raw data range is
 * not strictly 0-255.
 */
export const stacNormalizedGrayscaleOlStyle = (): object => {
  return {
    color: ['array', ['band', 1], ['band', 1], ['band', 1], 1],
  };
};

/**
 * Converts a STAC render extension `renders` map to an array of `Style` objects
 * compatible with the Layer Manager style selector.
 */
export const getRendersAsStyles = (
  renders: Record<string, STACRenderEntry>,
): Style[] =>
  Object.entries(renders).map(([name, entry]) => ({
    name,
    title: entry.title ?? name,
    legendURL: '',
    abstract: '',
  }));
